import type { MouseEvent, RefObject } from 'react';
import styles from './index.module.less';
import { AccelerationFilm } from './acceleration-film';
import type { SupportedHtmlTags, Tools } from '../../../core/types';
import { Color, Size, FontType, Align } from '../../../core/enums';
import useToggle from '../../../utilities/useToggle';
import { Box } from '../../archived/box';
import { Row, Column } from '../../atoms/grid';
import IconButton from '../../archived/icon-button';
import { useBreakpoint } from '../../styleless/breakpoint';
import { Typography } from '../../archived/typography';
import Element from '../../../core/Element';

type Slide = {
  id?: string;
  header?: string;
  subHeader?: string;
  title?: string;
  text?: string;
  className?: string;
  glow?: 'lighter' | 'light';
  border?: boolean;
  horizontalVariant?: boolean;
  customization?: Customization;
  popoverOnClick?: (event: MouseEvent<HTMLElement>) => void;
  popoverOnClickRef?: RefObject<HTMLElement>;
  tooltip?: React.ReactNode;
  iconAriaLabel?: string;
};

interface Customization {
  headerTag?: keyof SupportedHtmlTags;
  headerColor?: Color;
  headerContainer?: keyof SupportedHtmlTags;
  subHeaderTag?: keyof SupportedHtmlTags;
  subHeaderColor?: Color;
  titleTag?: keyof SupportedHtmlTags;
  titleColor?: Color;
  textTag?: keyof SupportedHtmlTags;
  textColor?: Color;
  fontSizes?: {
    headerFontSize: number;
    subHeaderFontSize: number;
  };
}

interface HowYouEarnTilesProps {
  data: {
    slides: Slide[];
  };
  qeId?: string;
  $glow?: 'lighter' | 'light';
  $border?: boolean;
  showFloatingBanner?: boolean;
  customization?: Customization;
  v3TileStyles?: boolean;
  tools?: {
    left: Tools;
    right: Tools;
  };
}

const defaultCustomization: Customization = {
  headerTag: 'p',
  headerColor: Color.DeepBlue,
  headerContainer: 'h3',
  subHeaderTag: 'p',
  subHeaderColor: Color.DeepBlue,
  titleTag: 'p',
  titleColor: Color.DeepBlue,
  textTag: 'div',
  textColor: Color.Gray06,
  fontSizes: {
    headerFontSize: 26,
    subHeaderFontSize: 10,
  },
};

const SingleSlide = ({
  header,
  subHeader,
  title,
  text,
  glow,
  border,
  horizontalVariant,
  customization: {
    headerTag,
    headerColor,
    headerContainer,
    subHeaderTag,
    subHeaderColor,
    titleTag,
    titleColor,
    textTag,
    textColor,
  } = defaultCustomization,
  popoverOnClick,
  popoverOnClickRef,
  tooltip,
  iconAriaLabel,
}: Slide): React.ReactElement => {
  const margin = horizontalVariant ? 20 : 0;
  const [isOpen, toggle] = useToggle(false);

  const handleClick = (event: MouseEvent<HTMLElement>) => {
    toggle();
    popoverOnClick?.(event);
  };

  const screenReaderText = `${header || ''} ${subHeader || ''} ${title || ''}`;

  return (
    <Box
      className={styles.item}
      $border={border}
      $glow={glow}
      theme={{ rounded: Size.SM, marginHorizontal: 5 }}
    >
      {!horizontalVariant ? (
        <Element
          theme={{
            margin: margin,
          }}
        >
          <Element
            theme={{
              marginHorizontal: margin,
            }}
          >
            <Element
              Component={headerContainer}
              theme={{
                margin: 0,
                padding: 0,
              }}
            >
              <Element theme={{ visuallyHidden: true }}>
                {screenReaderText}
              </Element>
              <Element
                Component={headerTag}
                aria-hidden={true}
                theme={{
                  color: headerColor,
                  textHeading: 6,
                  fontType: FontType.BentonBold,
                  textAlign: Align.Center,
                  marginBottom: 0,
                  marginTop: 20,
                  marginLeft: 15,
                  marginRight: 15,
                }}
              >
                {header}
              </Element>
              <Element>
                <Element
                  Component={subHeaderTag}
                  aria-hidden={true}
                  theme={{
                    color: subHeaderColor,
                    textLegal: 1,
                    textAlign: Align.Center,
                    marginTop: 0,
                    marginLeft: 15,
                    marginRight: 15,
                  }}
                >
                  {subHeader}
                </Element>
              </Element>

              <Element
                theme={{
                  marginHorizontal: margin,
                }}
              >
                <Element
                  Component={titleTag}
                  aria-hidden={true}
                  theme={{
                    color: titleColor,
                    textHeading: 4,
                    textAlign: Align.Center,
                    fontType: FontType.BentonMedium,
                    marginBottom: 10,
                    marginTop: 10,
                    marginLeft: 15,
                    marginRight: 15,
                  }}
                >
                  {title}
                </Element>
              </Element>
            </Element>
            {text && (
              <Element
                Component={textTag}
                theme={{
                  textBody: 1,
                  fontType: FontType.BentonRegular,
                  marginLeft: 15,
                  marginRight: 15,
                  marginBottom: 30,
                  textAlign: Align.Center,
                }}
              >
                <Element Component="span" theme={{ color: textColor }}>
                  {text}
                </Element>
                {!tooltip && popoverOnClick && (
                  <span className={styles.popover}>
                    <IconButton
                      directory="dls"
                      imageName="dls-glyph-info"
                      expanded={isOpen}
                      theme={{ color: Color.BrightBlue }}
                      $size={Size.SM}
                      onClick={handleClick}
                      ariaLabel={iconAriaLabel ?? ''}
                      ref={popoverOnClickRef}
                    />
                  </span>
                )}
                {tooltip}
              </Element>
            )}
          </Element>
        </Element>
      ) : (
        <Element
          className={styles.horizontalVariantContainer}
          theme={{
            margin: margin,
          }}
        >
          <Row
            theme={{
              marginHorizontal: margin,
            }}
          >
            <Column
              $md="3"
              Component={headerContainer}
              theme={{
                margin: 0,
                padding: 0,
              }}
            >
              <Element theme={{ visuallyHidden: true }}>
                {screenReaderText}
              </Element>
              <Element
                Component={headerTag}
                aria-hidden={true}
                theme={{
                  color: headerColor,
                  textHeading: 6,
                  fontType: FontType.BentonBold,
                  textAlign: Align.Center,
                  marginBottom: 0,
                  marginTop: 10,
                  marginLeft: 15,
                  marginRight: 15,
                }}
              >
                {header}
              </Element>
              <Element>
                <Element
                  Component={subHeaderTag}
                  aria-hidden={true}
                  theme={{
                    color: subHeaderColor,
                    textLegal: 1,
                    textAlign: Align.Center,
                    marginTop: 0,
                    marginLeft: 15,
                    marginRight: 15,
                  }}
                >
                  {subHeader}
                </Element>
              </Element>
            </Column>
            <Column $md="9">
              <Element
                Component={titleTag}
                aria-hidden={true}
                theme={{
                  color: titleColor,
                  textHeading: 4,
                  textAlign: Align.Left,
                  fontType: FontType.BentonMedium,
                  marginBottom: 10,
                  marginTop: 15,
                  marginLeft: 15,
                  marginRight: 15,
                }}
              >
                {title}
              </Element>
              {text && (
                <Element
                  Component={textTag}
                  theme={{
                    textBody: 1,
                    fontType: FontType.BentonRegular,
                    marginLeft: 15,
                    marginRight: 40,
                    marginBottom: 10,
                    textAlign: Align.Left,
                  }}
                >
                  <Element Component="span" theme={{ color: textColor }}>
                    {text}
                  </Element>
                  {!tooltip && popoverOnClick && (
                    <span className={styles.popover}>
                      <IconButton
                        directory="dls"
                        imageName="dls-glyph-info"
                        expanded={isOpen}
                        theme={{ color: Color.BrightBlue }}
                        $size={Size.SM}
                        onClick={handleClick}
                        ariaLabel={iconAriaLabel ?? ''}
                        ref={popoverOnClickRef}
                      />
                    </span>
                  )}
                  {tooltip}
                </Element>
              )}
            </Column>
          </Row>
        </Element>
      )}
    </Box>
  );
};

const MobileTile = ({
  header,
  subHeader,
  title,
  text,
  customization: {
    headerTag,
    headerColor,
    headerContainer,
    subHeaderTag,
    subHeaderColor,
    titleTag,
    titleColor,
    textTag,
    textColor,
    fontSizes,
  } = defaultCustomization,
  popoverOnClick,
  popoverOnClickRef,
  tooltip,
  iconAriaLabel,
}: Slide): React.ReactElement => {
  const [isOpen, toggle] = useToggle(false);

  const handleClick = (event: MouseEvent<HTMLElement>) => {
    toggle();
    popoverOnClick?.(event);
  };

  const screenReaderText = `${header || ''} ${subHeader || ''} ${title || ''}`;

  return (
    <Element>
      <Row>
        <Column $sm="3">
          {header && (
            <Element
              theme={{
                textAlign: Align.Center,
              }}
              className={styles.leftIcon}
            >
              <Element className={styles.header}>
                <Typography
                  Component={headerTag}
                  aria-hidden={true}
                  $family={FontType.BentonBold}
                  $size={fontSizes?.headerFontSize ?? 12}
                  theme={{
                    marginBottom: 0,
                    marginTop: 20,
                    color: headerColor,
                  }}
                >
                  {header}
                </Typography>
              </Element>
              <Element className={styles.subHeader}>
                <Typography
                  Component={subHeaderTag}
                  aria-hidden={true}
                  $family={FontType.BentonRegular}
                  $size={fontSizes?.subHeaderFontSize ?? 12}
                  theme={{
                    marginTop: 0,
                    fontType: FontType.BentonRegular,
                    color: subHeaderColor,
                  }}
                >
                  {subHeader}
                </Typography>
              </Element>
            </Element>
          )}
        </Column>
        <Column $sm="9" $smPosition="last">
          <Element
            Component={headerContainer}
            theme={{
              margin: 0,
              padding: 0,
            }}
          >
            <Element theme={{ visuallyHidden: true }}>
              {screenReaderText}
            </Element>
            <Element
              Component={titleTag}
              aria-hidden={true}
              theme={{
                color: titleColor,
                textHeading: 4,
                fontType: FontType.BentonMedium,
                marginTop: 20,
                marginBottom: 10,
              }}
            >
              {title}
            </Element>
          </Element>
          {text && (
            <Element
              Component={textTag}
              theme={{
                textBody: 1,
                fontType: FontType.BentonRegular,
              }}
            >
              <Element Component="span" theme={{ color: textColor }}>
                {text}
              </Element>
              {!tooltip && popoverOnClick && (
                <span className={styles.popover}>
                  <IconButton
                    directory="dls"
                    imageName="dls-glyph-info"
                    theme={{ color: Color.BrightBlue }}
                    $size={Size.SM}
                    expanded={isOpen}
                    onClick={handleClick}
                    ariaLabel={iconAriaLabel ?? ''}
                    ref={popoverOnClickRef}
                  />
                </span>
              )}
              {tooltip}
            </Element>
          )}
        </Column>
      </Row>
    </Element>
  );
};

export const HowYouEarnTiles = ({
  data: { slides },
  $glow = 'lighter',
  $border = true,
  qeId = 'HowYouEarnTiles',
  showFloatingBanner,
  customization = defaultCustomization,
  v3TileStyles,
  tools,
}: HowYouEarnTilesProps): React.ReactElement => {
  const customizations = { ...defaultCustomization, ...customization };

  const { sm } = useBreakpoint();
  const Slides = () => {
    const head = showFloatingBanner && slides[0] ? slides[0] : null;
    const floatingBanner = head ? (
      <Element
        theme={{
          marginBottom: 5,
        }}
      >
        <SingleSlide
          header={head.header}
          subHeader={head.subHeader}
          title={head.title}
          text={head.text}
          glow={$glow}
          border={$border}
          horizontalVariant
          customization={customizations}
          popoverOnClick={head.popoverOnClick}
          popoverOnClickRef={head.popoverOnClickRef}
          tooltip={head.tooltip}
          iconAriaLabel={head.iconAriaLabel}
        />
      </Element>
    ) : null;
    const remainder = showFloatingBanner ? slides.slice(1) : slides;

    return (
      <>
        {floatingBanner}
        <AccelerationFilm tools={tools}>
          {remainder.map(
            ({
              id,
              header,
              subHeader,
              title,
              text,
              popoverOnClick,
              popoverOnClickRef,
              tooltip,
              iconAriaLabel,
            }) => (
              <SingleSlide
                key={id ?? title}
                header={header}
                subHeader={subHeader}
                title={title}
                text={text}
                glow={$glow}
                border={$border}
                customization={customizations}
                popoverOnClick={popoverOnClick}
                popoverOnClickRef={popoverOnClickRef}
                tooltip={tooltip}
                iconAriaLabel={iconAriaLabel}
              />
            )
          )}
        </AccelerationFilm>
      </>
    );
  };

  return (
    <div data-qe-id={qeId} className={styles.root}>
      {sm ? (
        <Box
          $border={$border}
          $glow={v3TileStyles ? undefined : $glow}
          theme={{
            rounded: v3TileStyles ? Size.XS : Size.MD,
            padding: 10,
            margin: 0,
          }}
        >
          {slides.map(
            ({
              id,
              header,
              subHeader,
              title,
              text,
              popoverOnClick,
              popoverOnClickRef,
              tooltip,
              iconAriaLabel,
            }) => (
              <MobileTile
                key={id ?? title}
                header={header}
                subHeader={subHeader}
                title={title}
                text={text}
                customization={customizations}
                popoverOnClick={popoverOnClick}
                popoverOnClickRef={popoverOnClickRef}
                tooltip={tooltip}
                iconAriaLabel={iconAriaLabel}
              />
            )
          )}
        </Box>
      ) : (
        <Slides />
      )}
    </div>
  );
};
